import { useState, useCallback, useMemo } from "react";
import {
  ChallengeTableChallenge,
  ChallengeTableProgramVersionItem,
} from "../../../api/types/dtos/challenge/ChallengeDto";

export interface UseColumnVisibilityParams {
  data?: {
    challenges: ChallengeTableChallenge[];
    programVersionItems: ChallengeTableProgramVersionItem[];
  };
  programs: { id: string; name: string }[];
  programVersions: { id: string; programId: string; name: string }[];
}

export const useColumnVisibility = ({ data, programs, programVersions }: UseColumnVisibilityParams) => {
  const [columnSelectorOpen, setColumnSelectorOpen] = useState<boolean>(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>({});
  const [expandedLevels, setExpandedLevels] = useState<Set<string>>(new Set());

  const programVersionItems = useMemo(() => data?.programVersionItems || [], [data?.programVersionItems]);

  // Build mapping from versionId to program info
  const versionToProgramMap = useMemo(() => {
    const map: Record<string, { programId: string; programName: string; versionName: string }> = {};
    programVersions.forEach((version) => {
      const program = programs.find((p) => p.id === version.programId);
      map[version.id] = {
        programId: version.programId,
        programName: program?.name || "Programa desconocido",
        versionName: version.name,
      };
    });
    return map;
  }, [programs, programVersions]);

  // Group program version items by Program -> Level for column visibility
  const groupedByProgram = useMemo(() => {
    const programGroups: Record<string, {
      name: string;
      levels: Record<string, { name: string; items: ChallengeTableProgramVersionItem[] }>;
    }> = {};

    programVersionItems.forEach((pvi) => {
      const versionInfo = versionToProgramMap[pvi.versionId];
      const programId = versionInfo?.programId || "unknown";
      const programName = versionInfo?.programName || "Programa desconocido";
      const levelId = pvi.programLevelId || "ungrouped";
      const levelName = pvi.programLevelName || "Sin nivel";

      if (!programGroups[programId]) {
        programGroups[programId] = { name: programName, levels: {} };
      }
      if (!programGroups[programId].levels[levelId]) {
        programGroups[programId].levels[levelId] = { name: levelName, items: [] };
      }
      programGroups[programId].levels[levelId].items.push(pvi);
    });

    // Sort items within each level
    Object.values(programGroups).forEach((program) => {
      Object.values(program.levels).forEach((level) => {
        level.items.sort((a, b) =>
          (a.cpNumber || "").localeCompare(b.cpNumber || "", undefined, {
            numeric: true,
          }),
        );
      });
    });

    return programGroups;
  }, [programVersionItems, versionToProgramMap]);

  // Group by level for backward compatibility
  const groupedByLevel = useMemo(() => {
    const groups: Record<string, { name: string; items: ChallengeTableProgramVersionItem[] }> = {};
    programVersionItems.forEach((pvi) => {
      const levelId = pvi.programLevelId || "ungrouped";
      const levelName = pvi.programLevelName || "Sin nivel";
      if (!groups[levelId]) {
        groups[levelId] = { name: levelName, items: [] };
      }
      groups[levelId].items.push(pvi);
    });
    // Sort items within each group
    Object.values(groups).forEach((group) => {
      group.items.sort((a, b) =>
        (a.cpNumber || "").localeCompare(b.cpNumber || "", undefined, {
          numeric: true,
        }),
      );
    });
    return groups;
  }, [programVersionItems]);

  // Helper to get ALL challenge column fields (all PVIs)
  const getAllChallengeColumnFields = useCallback((): string[] => {
    return programVersionItems.map((pvi) => `pvi_${pvi.id}`);
  }, [programVersionItems]);

  // Check if all challenge columns are visible
  const isChallengeVisible = useCallback((): boolean => {
    const fields = getAllChallengeColumnFields();
    if (fields.length === 0) return true;
    return fields.every((field) => columnVisibilityModel[field] !== false);
  }, [getAllChallengeColumnFields, columnVisibilityModel]);

  // Check if some (but not all) challenge columns are visible
  const isChallengePartiallyVisible = useCallback((): boolean => {
    const fields = getAllChallengeColumnFields();
    if (fields.length === 0) return false;
    const visibleCount = fields.filter(
      (field) => columnVisibilityModel[field] !== false,
    ).length;
    return visibleCount > 0 && visibleCount < fields.length;
  }, [getAllChallengeColumnFields, columnVisibilityModel]);

  // Toggle visibility for all challenge columns
  const handleToggleChallenge = useCallback(() => {
    const fields = getAllChallengeColumnFields();
    const isVisible = isChallengeVisible();
    setColumnVisibilityModel((prev) => {
      const next = { ...prev };
      fields.forEach((field) => {
        next[field] = !isVisible;
      });
      return next;
    });
  }, [getAllChallengeColumnFields, isChallengeVisible]);

  // Helper to get all column fields for a program
  const getProgramColumnFields = useCallback(
    (programId: string): string[] => {
      const program = groupedByProgram[programId];
      if (!program) return [];
      const fields: string[] = [];
      Object.values(program.levels).forEach((level) => {
        level.items.forEach((pvi) => {
          fields.push(`pvi_${pvi.id}`);
        });
      });
      return fields;
    },
    [groupedByProgram],
  );

  // Check if all columns in a program are visible
  const isProgramVisible = useCallback(
    (programId: string): boolean => {
      const fields = getProgramColumnFields(programId);
      if (fields.length === 0) return true;
      return fields.every((field) => columnVisibilityModel[field] !== false);
    },
    [getProgramColumnFields, columnVisibilityModel],
  );

  // Check if some (but not all) columns in a program are visible
  const isProgramPartiallyVisible = useCallback(
    (programId: string): boolean => {
      const fields = getProgramColumnFields(programId);
      if (fields.length === 0) return false;
      const visibleCount = fields.filter(
        (field) => columnVisibilityModel[field] !== false,
      ).length;
      return visibleCount > 0 && visibleCount < fields.length;
    },
    [getProgramColumnFields, columnVisibilityModel],
  );

  // Toggle visibility for all columns in a program
  const handleToggleProgram = useCallback(
    (programId: string) => {
      const fields = getProgramColumnFields(programId);
      const isVisible = isProgramVisible(programId);
      setColumnVisibilityModel((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          next[field] = !isVisible;
        });
        return next;
      });
    },
    [getProgramColumnFields, isProgramVisible],
  );

  // Helper to get all column fields for a level within a specific program
  const getProgramLevelColumnFields = useCallback(
    (programId: string, levelId: string): string[] => {
      const program = groupedByProgram[programId];
      if (!program) return [];
      const level = program.levels[levelId];
      if (!level) return [];
      return level.items.map((pvi) => `pvi_${pvi.id}`);
    },
    [groupedByProgram],
  );

  // Check if all columns in a level within a program are visible
  const isProgramLevelVisible = useCallback(
    (programId: string, levelId: string): boolean => {
      const fields = getProgramLevelColumnFields(programId, levelId);
      if (fields.length === 0) return true;
      return fields.every((field) => columnVisibilityModel[field] !== false);
    },
    [getProgramLevelColumnFields, columnVisibilityModel],
  );

  // Check if some (but not all) columns in a level within a program are visible
  const isProgramLevelPartiallyVisible = useCallback(
    (programId: string, levelId: string): boolean => {
      const fields = getProgramLevelColumnFields(programId, levelId);
      if (fields.length === 0) return false;
      const visibleCount = fields.filter(
        (field) => columnVisibilityModel[field] !== false,
      ).length;
      return visibleCount > 0 && visibleCount < fields.length;
    },
    [getProgramLevelColumnFields, columnVisibilityModel],
  );

  // Toggle visibility for all columns in a level within a program
  const handleToggleProgramLevel = useCallback(
    (programId: string, levelId: string) => {
      const fields = getProgramLevelColumnFields(programId, levelId);
      const isVisible = isProgramLevelVisible(programId, levelId);
      setColumnVisibilityModel((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          next[field] = !isVisible;
        });
        return next;
      });
    },
    [getProgramLevelColumnFields, isProgramLevelVisible],
  );

  // Helper to get all column fields for a level
  const getLevelColumnFields = useCallback(
    (levelId: string): string[] => {
      const group = groupedByLevel[levelId];
      if (!group) return [];
      return group.items.map((pvi) => `pvi_${pvi.id}`);
    },
    [groupedByLevel],
  );

  // Check if all columns in a level are visible
  const isLevelVisible = useCallback(
    (levelId: string): boolean => {
      const fields = getLevelColumnFields(levelId);
      if (fields.length === 0) return true;
      return fields.every((field) => columnVisibilityModel[field] !== false);
    },
    [getLevelColumnFields, columnVisibilityModel],
  );

  // Check if some (but not all) columns in a level are visible
  const isLevelPartiallyVisible = useCallback(
    (levelId: string): boolean => {
      const fields = getLevelColumnFields(levelId);
      if (fields.length === 0) return false;
      const visibleCount = fields.filter(
        (field) => columnVisibilityModel[field] !== false,
      ).length;
      return visibleCount > 0 && visibleCount < fields.length;
    },
    [getLevelColumnFields, columnVisibilityModel],
  );

  // Toggle visibility for all columns in a level
  const handleToggleLevel = useCallback(
    (levelId: string) => {
      const fields = getLevelColumnFields(levelId);
      const isVisible = isLevelVisible(levelId);
      setColumnVisibilityModel((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          next[field] = !isVisible;
        });
        return next;
      });
    },
    [getLevelColumnFields, isLevelVisible],
  );

  // Toggle visibility for a single column
  const handleToggleColumn = useCallback((field: string) => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      [field]: prev[field] === false ? true : false,
    }));
  }, []);

  // Toggle level expansion in drawer
  const handleToggleLevelExpand = useCallback((levelId: string) => {
    setExpandedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(levelId)) {
        next.delete(levelId);
      } else {
        next.add(levelId);
      }
      return next;
    });
  }, []);

  return {
    columnSelectorOpen,
    setColumnSelectorOpen,
    columnVisibilityModel,
    setColumnVisibilityModel,
    expandedLevels,
    setExpandedLevels,
    groupedByProgram,
    groupedByLevel,
    isChallengeVisible,
    isChallengePartiallyVisible,
    isProgramVisible,
    isProgramPartiallyVisible,
    isProgramLevelVisible,
    isProgramLevelPartiallyVisible,
    isLevelVisible,
    isLevelPartiallyVisible,
    handleToggleChallenge,
    handleToggleProgram,
    handleToggleProgramLevel,
    handleToggleLevel,
    handleToggleColumn,
    handleToggleLevelExpand,
  };
};
